import type { FloatingPosition } from '@mantine/core/lib/Floating';
import { DEFAULT_ITEM_SIZE } from '@repo/common/components/MultiSelector/constants';
import { useMemo } from 'react';
import {
	useAuthUser,
	type IIntegration,
	type ISecodaEntity,
	type IUser,
} from '../../api';
import {
	useExtendedUserList,
	useGuestUserList,
} from '../../api/hooks/user/useExtendedUserList';
import type { DjangoValueType } from '../../pages/TemplatePage/types';
import { addToSubscribers, getAddedItems } from '../../utils/subscriberHelpers';
import type { SidebarEntityKeys } from '../EntityPageLayout/types';
import { getOwnerAndGroupSelectorOptions } from '../EntityPageLayout/utils';
import { externalUsers } from '../UserAvatar/helpers';
import { PropertySelector } from './PropertySelector';

export interface OwnersPropertySelectorProps {
	entity: ISecodaEntity;
	viewerOfEntity: boolean;
	integration?: IIntegration;
	handleEntityChange: (
		key: SidebarEntityKeys
	) => (value: DjangoValueType, saveRemotely?: boolean) => void;
	preventNavigate?: boolean;
	position?: FloatingPosition;
	withinPortal?: boolean;
	noMaxHeightDropdown?: boolean;
}

export function OwnersPropertySelector({
	entity,
	viewerOfEntity,
	integration,
	handleEntityChange,
	preventNavigate = false,
	position,
	withinPortal,
	noMaxHeightDropdown = false,
}: OwnersPropertySelectorProps) {
	const { isGuestUser } = useAuthUser();

	const {
		userGroups: groups,
		disabledUsers,
		activeUsers,
		serviceAccountsActive,
		isLoading: isUserLoading,
	} = useExtendedUserList();
	const { usersVisibleToGuests, isLoading: isUsersVisibleToGuestsLoading } =
		useGuestUserList();

	const forceSourceOwners = !!integration?.force_source_owners;

	const serviceAccountOptions = useMemo(
		() =>
			(serviceAccountsActive?.filter((serviceAccount) =>
				entity.owners.includes(serviceAccount.id)
			) as unknown as IUser[]) ?? [],
		[entity.owners, serviceAccountsActive]
	);

	const ownerOptions = useMemo(
		() =>
			isGuestUser
				? ((usersVisibleToGuests ?? []) as unknown as IUser[])
				: [
						...((disabledUsers ?? []) as unknown as IUser[]),
						...((activeUsers ?? []) as unknown as IUser[]),
						...serviceAccountOptions,
						...externalUsers(
							activeUsers ?? [],
							serviceAccountsActive ?? [],
							entity
						),
					].filter((owner) =>
						forceSourceOwners ? entity.owners.includes(owner.id) : true
					),
		[
			activeUsers,
			disabledUsers,
			entity,
			isGuestUser,
			forceSourceOwners,
			serviceAccountOptions,
			serviceAccountsActive,
			usersVisibleToGuests,
		]
	);

	const handleOwnersChange = useMemo(
		() => (values: DjangoValueType) => {
			handleEntityChange('owners')(values, true);

			// Optimistically add new owners to subscribers
			const currentOwners = entity.owners ?? [];
			const newOwners = (values as string[]) ?? [];
			const addedOwners = getAddedItems(currentOwners, newOwners);

			if (addedOwners.length > 0) {
				const currentSubscribers = (entity.subscribers ?? []) as string[];
				const updatedSubscribers = addToSubscribers(
					currentSubscribers,
					addedOwners
				);
				handleEntityChange('subscribers')(updatedSubscribers, false);
			}
		},
		[entity.owners, entity.subscribers, handleEntityChange]
	);

	const handleOwnerGroupsChange = useMemo(
		() => (values: DjangoValueType) => {
			handleEntityChange('owners_groups')(values, true);

			// Optimistically add new owner groups to subscriber groups
			const currentOwnerGroups = entity.owners_groups ?? [];
			const newOwnerGroups = (values as string[]) ?? [];
			const addedOwnerGroups = getAddedItems(
				currentOwnerGroups,
				newOwnerGroups
			);

			if (addedOwnerGroups.length > 0) {
				const currentSubscriberGroups = (entity.subscribers_groups ??
					[]) as string[];
				const updatedSubscriberGroups = addToSubscribers(
					currentSubscriberGroups,
					addedOwnerGroups
				);
				handleEntityChange('subscribers_groups')(
					updatedSubscriberGroups,
					false
				);
			}
		},
		[entity.owners_groups, entity.subscribers_groups, handleEntityChange]
	);

	const owners = useMemo(
		() =>
			getOwnerAndGroupSelectorOptions(
				ownerOptions,
				!forceSourceOwners && groups ? groups : [],
				handleOwnersChange,
				handleOwnerGroupsChange
			).map((owner) => ({
				...owner,
				itemTypeName: 'owners',
				entityIntegrationId: integration?.id,
				maintainProp: forceSourceOwners,
				navigateTo: preventNavigate ? undefined : owner.navigateTo,
			})),
		[
			groups,
			handleOwnersChange,
			handleOwnerGroupsChange,
			integration?.id,
			forceSourceOwners,
			ownerOptions,
			preventNavigate,
		]
	);

	const selectedOwners = useMemo(
		() => [...(entity?.owners ?? []), ...(entity?.owners_groups ?? [])],
		[entity]
	);

	const serviceAccountOwners = useMemo(
		() =>
			entity.owners.filter((owner) =>
				serviceAccountsActive?.find(
					(serviceAccount: { id: string }) => serviceAccount.id === owner
				)
			),
		[entity.owners, serviceAccountsActive]
	);

	return (
		<PropertySelector
			isLoading={isUserLoading || isUsersVisibleToGuestsLoading}
			readOnly={forceSourceOwners || viewerOfEntity}
			selected={selectedOwners}
			inheritedValues={forceSourceOwners ? serviceAccountOwners : undefined}
			type="multi"
			value="owners"
			iconType="avatar"
			isViewerUser={viewerOfEntity}
			options={owners}
			placeholder={
				forceSourceOwners || viewerOfEntity ? 'No owners' : 'Add owner'
			}
			placeholderIconName="user"
			position={position}
			withinPortal={withinPortal}
			noMaxHeightDropdown={noMaxHeightDropdown}
			virtuosoHeight={`min(70vh, ${(owners.length || 8) * DEFAULT_ITEM_SIZE}px)`}
		/>
	);
}
